import React, { useState, useEffect } from 'react'
import {
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
} from '@material-ui/core'
import { useQuery } from 'react-query'
import { useHistory } from 'react-router-dom'
import getCompetitions from '../../Apis/competition/getCompetitions'
import getCompetition from '../../Apis/competition/getCompetition'
import QueryKeys from '../../utils/constants/queryKeys'
import useUrlSearchParams from '../../hooks/useUrlSearchParams'
import useQueryParams from '../../hooks/useQueryParams'
import BrandingTab from '../CmsAdminPage/BrandingTab/BrandingTab'

export default function BrandingPage() {
  const history = useHistory()
  const params = useQueryParams()
  const { org: orgId } = useUrlSearchParams(['org']) || {}

  const [selectedCompetitionId, setSelectedCompetitionId] = useState(params.competition || '')
  const [selectedSeasonId, setSelectedSeasonId] = useState(params.season || '')
  const [editingType, setEditingType] = useState(params.type || null)
  // variant is its own query param, not folded into `type`; an old `?type=TeamShots` link
  // with no `variant` still opens, defaulting to Home.
  const [editingVariant, setEditingVariant] = useState(() => {
    if (params.variant !== undefined) {
      const parsed = Number(params.variant)
      if (Number.isInteger(parsed)) return parsed
    }
    return params.type === 'TeamShots' ? 1 : 0
  })

  const { data: competitions = [], isLoading: competitionsLoading } = useQuery(
    [QueryKeys.GET_COMPETITIONS, orgId],
    () => getCompetitions(orgId),
    { enabled: Boolean(orgId) },
  )

  const { data: competitionDetail, isLoading: seasonsLoading } = useQuery(
    [QueryKeys.GET_COMPETITION, orgId, selectedCompetitionId],
    () => getCompetition({ orgId, competitionId: selectedCompetitionId }),
    { enabled: Boolean(orgId && selectedCompetitionId) },
  )

  const seasons = competitionDetail?.seasons ?? []

  // Sync state → URL
  useEffect(() => {
    const qp = new URLSearchParams(history.location.search)
    if (selectedCompetitionId) qp.set('competition', selectedCompetitionId)
    else qp.delete('competition')
    if (selectedSeasonId) qp.set('season', selectedSeasonId)
    else qp.delete('season')
    if (editingType) {
      qp.set('type', editingType)
      qp.set('variant', String(editingVariant ?? 0))
    } else {
      qp.delete('type')
      qp.delete('variant')
    }
    const search = qp.toString()
    if (search !== new URLSearchParams(history.location.search).toString()) {
      history.replace({ search })
    }
  }, [selectedCompetitionId, selectedSeasonId, editingType, editingVariant, history])

  const handleCompetitionChange = (e) => {
    setSelectedCompetitionId(e.target.value)
    setSelectedSeasonId('')
    setEditingType(null)
    setEditingVariant(0)
  }

  const handleEditingChange = (type, variant = 0) => {
    setEditingType(type)
    setEditingVariant(type ? variant : 0)
  }

  if (!orgId) {
    return (
      <div style={{ padding: 24 }}>
        <Typography color='error' variant='body2'>
          Missing org parameter. Please navigate to Branding from an organisation page.
        </Typography>
      </div>
    )
  }

  return (
    <div style={{ padding: 24 }}>
      <Typography variant='h5' gutterBottom>
        Branding
      </Typography>
      <Typography variant='body2' color='textSecondary' style={{ marginBottom: 24 }}>
        Configure custom overlay templates for broadcast graphics.
      </Typography>

      <div style={{ display: 'flex', gap: 16, marginBottom: 32, alignItems: 'flex-end' }}>
        <FormControl variant='outlined' style={{ minWidth: 240 }}>
          <InputLabel>Competition</InputLabel>
          <Select
            value={selectedCompetitionId}
            onChange={handleCompetitionChange}
            label='Competition'
            disabled={competitionsLoading}
          >
            {competitionsLoading
              ? <MenuItem disabled><CircularProgress size={16} /></MenuItem>
              : competitions.map((c) => (
                  <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                ))
            }
          </Select>
        </FormControl>

        <FormControl variant='outlined' style={{ minWidth: 240 }} disabled={!selectedCompetitionId}>
          <InputLabel>Season</InputLabel>
          <Select
            value={selectedSeasonId}
            onChange={(e) => setSelectedSeasonId(e.target.value)}
            label='Season'
          >
            {seasonsLoading
              ? <MenuItem disabled><CircularProgress size={16} /></MenuItem>
              : seasons.map((s) => (
                  <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
                ))
            }
          </Select>
        </FormControl>
      </div>

      {selectedSeasonId && (
        <BrandingTab
          orgId={orgId}
          seasonId={selectedSeasonId}
          editingType={editingType}
          editingVariant={editingVariant}
          onEditingChange={handleEditingChange}
        />
      )}
    </div>
  )
}
